import { config } from 'utils/config';
import { buildSignatureHtml, buildSignaturePlainText } from './signatureTemplate';

// Shared signature logic for MyBusinessCardScreen (self-service) and SignatureViewer
// (admin): featured-row resolution, phone formatting, image URL building, and the
// clipboard write.

// Mirrors the backend FormatPhoneNA so on-screen values match the rendered images:
// NANP-shaped digit strings are normalized to E.164 first ("5734292391" and
// "573-429-2391" both become "+1 (573) 429-2391"); anything else renders as entered.
export const formatPhoneForSignature = (value) => {
  if (!value) {
    return '';
  }

  let phone = value;
  if (!phone.startsWith('+')) {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      phone = `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      phone = `+${digits}`;
    }
  }

  if (phone.length < 10 || /\D/.test(phone.slice(-10))) {
    return value;
  }

  const last10 = phone.slice(-10);
  return `${phone.slice(0, -10)} (${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`.trim();
};

export const isMobilePhoneRow = (row) => /mobile|cell/i.test(row.NomTipoDBCContactLabel || '');

// Label-based defaults for the two phone slots when the user never made a pick
export const findDefaultPhoneRows = (phones) => {
  const mobileRow = phones.find(isMobilePhoneRow) || null;
  const officeRow =
    phones.find((row) => row !== mobileRow && /work|office|direct/i.test(row.NomTipoDBCContactLabel || '')) || null;
  return { mobileRow, officeRow };
};

// Saved selection semantics: a positive id = that row; 0 = explicitly "None"
// (must stick — sales reps hide numbers on purpose); NULL = never chosen, so the
// label-based default applies. A dangling id (row deleted) also falls to default.
export const resolveFeaturedRow = (savedId, rows, defaultRow) => {
  if (savedId === 0) {
    return null;
  }

  if (typeof savedId === 'number' && savedId > 0) {
    return rows.find((row) => row.ContactId === savedId) || defaultRow;
  }

  return defaultRow;
};

// Builds the object buildSignatureHtml/buildSignaturePlainText consume. The v=
// cache-buster forces a fresh image fetch after edits and at paste time (Outlook then
// embeds the images as attachments); pass Date.now() as version when copying so
// Outlook never reuses cached copies. The images reflect SAVED data only.
export const buildSignatureData = ({ ClaUsuarioBC, FullName, JobTitle, emailRow, mobileRow, officeRow, version }) => {
  const barParams =
    `ClaUsuarioBC=${ClaUsuarioBC}` +
    `&EmailContactId=${emailRow ? emailRow.ContactId : ''}` +
    `&MobileContactId=${mobileRow ? mobileRow.ContactId : ''}` +
    `&OfficeContactId=${officeRow ? officeRow.ContactId : ''}`;

  return {
    FullName,
    JobTitle,
    Email: emailRow ? emailRow.Valor : '',
    MobilePhone: mobileRow ? formatPhoneForSignature(mobileRow.Valor) : '',
    OfficePhone: officeRow ? formatPhoneForSignature(officeRow.Valor) : '',
    // Raw values feed the tel: links in the template
    MobilePhoneRaw: mobileRow ? mobileRow.Valor : '',
    OfficePhoneRaw: officeRow ? officeRow.Valor : '',
    BannerLeftUrl: `${config.UrlApiProject}QRCode/GetSignatureBanner?ClaUsuarioBC=${ClaUsuarioBC}&Part=left&v=${version}`,
    BannerRightUrl: `${config.UrlApiProject}QRCode/GetSignatureBanner?ClaUsuarioBC=${ClaUsuarioBC}&Part=right&v=${version}`,
    EmailBarUrl: `${config.UrlApiProject}QRCode/GetSignatureContactBar?${barParams}&Segment=email&v=${version}`,
    PhoneBarUrl: `${config.UrlApiProject}QRCode/GetSignatureContactBar?${barParams}&Segment=phone&v=${version}`,
    MobileBarUrl: `${config.UrlApiProject}QRCode/GetSignatureContactBar?${barParams}&Segment=mobile&v=${version}`,
    OfficeBarUrl: `${config.UrlApiProject}QRCode/GetSignatureContactBar?${barParams}&Segment=office&v=${version}`,
    LockupBarUrl: `${config.UrlApiProject}QRCode/GetSignatureContactBar?${barParams}&Segment=lockup&v=${version}`,
    AssetBaseUrl: config.UrlProject.endsWith('/') ? config.UrlProject : `${config.UrlProject}/`,
  };
};

// Iframe isolation: the preview renders in its own clean document, like a mail
// client — the app's global CSS cannot distort the signature layout
export const buildPreviewSrcDoc = (data) =>
  `<!doctype html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:0;background:#fafafa;">${buildSignatureHtml(data)}</body></html>`;

// Intercepting the copy event and calling setData writes our HTML to the clipboard
// VERBATIM. Both alternatives corrupt it: navigator.clipboard.write() sanitizes the
// HTML in Chromium (strips font-size/family, breaking the paste into Outlook), and
// plain selection copying re-serializes the fragment unreliably.
const copyViaCopyEvent = (html, plainText) => {
  let wroteData = false;

  const onCopy = (e) => {
    e.preventDefault();
    e.stopImmediatePropagation();
    e.clipboardData.setData('text/html', html);
    e.clipboardData.setData('text/plain', plainText);
    wroteData = true;
  };

  // A non-empty selection is required for execCommand('copy') to fire the event
  const container = document.createElement('div');
  container.textContent = '.';
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  document.body.appendChild(container);

  const range = document.createRange();
  range.selectNodeContents(container);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);

  document.addEventListener('copy', onCopy, true);
  try {
    document.execCommand('copy');
  } catch (error) {
    wroteData = false;
  } finally {
    document.removeEventListener('copy', onCopy, true);
    selection.removeAllRanges();
    document.body.removeChild(container);
  }

  return wroteData;
};

// Returns true when the signature reached the clipboard
export const writeSignatureToClipboard = async (data) => {
  const html = buildSignatureHtml(data);
  const plainText = buildSignaturePlainText(data);

  if (copyViaCopyEvent(html, plainText)) {
    return true;
  }

  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
      }),
    ]);
    return true;
  } catch (error) {
    return false;
  }
};
